import React, { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ImageUploader } from "@/components/admin/ImageUploader";

const input = "w-full bg-background border border-border px-3 py-2 text-sm outline-none focus:border-foreground transition-colors";
const labelCls = "text-[11px] tracking-[0.12em] uppercase text-muted-foreground block mb-1.5";

const Card = ({ title, children }) => (
  <div className="border border-border bg-card">
    <div className="px-5 py-3 border-b border-border"><h3 className="font-display font-bold tracking-tight">{title}</h3></div>
    <div className="p-5 space-y-4">{children}</div>
  </div>
);

export default function AdminSettings() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setF(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try { const payload = { ...f, default_tax_rate: Number(f.default_tax_rate) || 0 }; delete payload._id; await api.put("/settings", payload); toast.success("Settings saved"); }
    catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  if (!f) return <div className="text-muted-foreground flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="font-display font-extrabold text-3xl tracking-tight">Settings</h1><p className="text-muted-foreground text-sm mt-1">Company defaults used across proposals & the public site.</p></div>
        <button onClick={save} disabled={saving} data-testid="save-settings" className="flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors disabled:opacity-50">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
      </div>

      <div className="space-y-6">
        <Card title="Company">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Company Name</label><input data-testid="settings-company" className={input} value={f.company_name || ""} onChange={(e) => set("company_name", e.target.value)} /></div>
            <div><label className={labelCls}>Tagline</label><input className={input} value={f.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></div>
            <div><label className={labelCls}>Email</label><input className={input} value={f.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className={labelCls}>Phone</label><input className={input} value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
            <div><label className={labelCls}>Website</label><input className={input} value={f.website || ""} onChange={(e) => set("website", e.target.value)} /></div>
            <div><label className={labelCls}>License No.</label><input className={input} value={f.license_no || ""} onChange={(e) => set("license_no", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Address</label><input className={input} value={f.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
          </div>
          <ImageUploader value={f.logo} onChange={(v) => set("logo", v)} label="Company Logo (used in navbar & proposals)" height="h-28" testid="settings-logo-upload" />
        </Card>

        <Card title="Proposal Defaults">
          <div><label className={labelCls}>Default Tax Rate (%)</label><input type="number" data-testid="settings-tax" className={`${input} max-w-[160px]`} value={f.default_tax_rate ?? 0} onChange={(e) => set("default_tax_rate", e.target.value)} /></div>
          <div><label className={labelCls}>Default Warranty</label><textarea rows={3} className={`${input} resize-none`} value={f.default_warranty || ""} onChange={(e) => set("default_warranty", e.target.value)} /></div>
          <div><label className={labelCls}>Default Terms & Conditions</label><textarea rows={4} className={`${input} resize-none`} value={f.default_terms || ""} onChange={(e) => set("default_terms", e.target.value)} /></div>
        </Card>
      </div>
    </div>
  );
}
